// A group's tasks: title and source reference, status, the step it is on, cost and when it last
// changed. Node names come from the group's pinned graph; a task on an older version may show a
// node's id instead. Sub-tasks (D66) sit under their parent behind an expand toggle; while any task
// has a parent the order is the tree's (newest activity first at each level), not a column sort.
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { useState } from "react";
import { Link } from "react-router";

import type { Graph } from "../../catalog/types";
import { money } from "../../costs/model";
import { ago } from "../../pages/format";
import { type Column, DataTable } from "../../ui/DataTable";
import type { SortValue } from "../../ui/sort";
import { Mono } from "../../ui/status";
import { nodeName, shownStatus } from "../display";
import { TaskStatusPill } from "../TaskStatusPill";
import { type TaskRow, taskRows } from "../links";
import type { Task } from "../model";
import { type Messages, useT } from "../../i18n";

type Copy = Messages["tasks"];

function currentStep(task: Task, graph: Graph | undefined, copy: Copy): string {
  if (task.status === "queued" && !task.current_node) return copy.notStarted;
  return nodeName(graph, task.active_step?.node ?? task.current_node);
}

function Title({ row, open, onToggle }: { row: TaskRow; open: boolean; onToggle: () => void }) {
  const copy = useT().tasks;
  const t = row.task;
  const Caret = open ? CaretDown : CaretRight;
  return (
    <div className="flex min-w-0 items-start gap-1.5" style={{ paddingInlineStart: `${row.depth * 1.25}rem` }}>
      {row.children > 0 ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={(open ? copy.group.hideSubTasks : copy.group.showSubTasks)(row.children, t.title)}
          className="mt-0.5 rounded p-0.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
        >
          <Caret weight="bold" className="h-3.5 w-3.5" aria-hidden />
        </button>
      ) : (
        <span className="w-[18px] shrink-0" aria-hidden />
      )}
      <div className="min-w-0">
        <Link to={`/tasks/${t.id}`} className="block max-w-[18rem] truncate font-semibold text-gray-900 hover:text-primary">
          {t.title}
        </Link>
        <span className="text-xs text-gray-500">
          {t.source_ref ? <Mono>#{t.source_ref}</Mono> : copy.source.manual}
          {t.child_count > 0 ? ` · ${copy.group.subTasks(t.child_count)}` : ""}
        </span>
      </div>
    </div>
  );
}

function columns(copy: Copy, graph: Graph | undefined, sortable: boolean, expanded: ReadonlySet<string>, toggle: (id: string) => void): Column<TaskRow>[] {
  const c = copy.columns;
  const sort = (f: (t: Task) => SortValue) => (sortable ? (r: TaskRow) => f(r.task) : undefined);
  return [
    {
      key: "title",
      header: c.task,
      sortValue: sort((t) => t.title.toLowerCase()),
      cell: (r) => <Title row={r} open={expanded.has(r.task.id)} onToggle={() => toggle(r.task.id)} />,
    },
    { key: "status", header: c.status, sortValue: sort((t) => shownStatus(t)), cell: (r) => <TaskStatusPill task={r.task} /> },
    { key: "step", header: c.currentStep, hideBelow: "md", cell: (r) => currentStep(r.task, graph, copy) },
    { key: "cost", header: c.cost, hideBelow: "sm", className: "text-end", sortValue: sort((t) => t.cost_usd), cell: (r) => <span className="tabular">{money(r.task.cost_usd)}</span> },
    { key: "updated", header: c.updated, hideBelow: "2xl", sortValue: sort((t) => t.updated_at), cell: (r) => <span className="whitespace-nowrap">{ago(r.task.updated_at)}</span> },
  ];
}

export function TasksTable({ tasks, graph }: { tasks: Task[]; graph: Graph | undefined }) {
  const copy = useT().tasks;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (id: string) =>
    setExpanded((s) => {
      const next = new Set(s);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  const linked = tasks.some((t) => t.parent_task_id);
  return (
    <DataTable
      label={copy.columns.tasks}
      columns={columns(copy, graph, !linked, expanded, toggle)}
      rows={taskRows(tasks, expanded)}
      rowKey={(r) => r.task.id}
      defaultSort={linked ? undefined : { key: "updated", dir: "desc" }}
    />
  );
}
